import React from 'react';
import { BottomSheet, Button, Card, Checkbox, Badge } from '@parsina/framework';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { SOAP_SECTIONS } from '../../screens/Visit/Visit.shared.js';
import { AiAnswerNote } from '../common/PracticeNotice.jsx';
import { urgencyKey, orderTypeKey } from '../../constants/vocabulary.js';

// The two review surfaces that stand between a model's draft and the record.
//
// core.md §3.4: «Every AI product is a draft or an answer… The student confirms
// what they keep through the same mutation they would use by hand.» So neither
// of these ever writes: they render what came back, side by side with what is
// already written, and hand a CLICK back to the caller.
//
// ── Why «اعمال» is per section, with a chooser ───────────────────────────
//
// A SOAP suggestion is four independent paragraphs and a student typically wants
// one of them. One «اعمال همه» button would make the useful case (take the O,
// keep my A) impossible, and would silently overwrite prose the student wrote
// while the model was thinking. Each section therefore offers جایگزینی (replace)
// and افزودن (append), and a section the student does not act on is left exactly
// as it was.
//
// The narrative leads, always: core.md §8.5 — the JSON payload is a best-effort
// parse and may be empty, and a sheet that rendered only the payload would show
// an empty sheet for a perfectly good answer.

const Narrative = ({ text }) => {
  if (!text) return null;
  return (
    <p style={{
      margin: '0 0 12px', fontSize: 13, lineHeight: 2, color: C.textPrimary,
      whiteSpace: 'pre-wrap',
    }}
    >
      {text}
    </p>
  );
};

/** «پیشنهاد دستیار» for the note — one card per section, with the current text beside it. */
export function SoapReviewSheet({ open, onClose, suggestion, current, onApply }) {
  const { t } = useTranslation();
  if (!open) return null;

  const sections = SOAP_SECTIONS.filter((s) => (suggestion?.[s.id] || '').trim());

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('visit.soap.reviewTitle')}
      fullHeight
      maxWidth={720}
      closeLabel={t('common.actions.close')}
      data-testid="soap-review-sheet"
    >
      <Narrative text={suggestion?.narrative} />
      {!sections.length && (
        <p style={{ fontSize: 13, color: CT.mute }}>{t('visit.soap.reviewEmpty')}</p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {sections.map((section) => (
          <Card key={section.id} style={{ padding: 12 }} data-testid={`soap-review-${section.id}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Badge variant="default">{section.id.toUpperCase()}</Badge>
              <span style={{ fontSize: 13, fontWeight: 700 }}>{t(section.labelKey)}</span>
            </div>
            <p style={{
              margin: 0, fontSize: 13, lineHeight: 2, whiteSpace: 'pre-wrap', color: C.textPrimary,
            }}
            >
              {suggestion[section.id]}
            </p>
            {(current?.[section.id] || '').trim() && (
              <div style={{
                marginTop: 8, padding: 8, borderRadius: 8, background: CT.bg,
                fontSize: 12, lineHeight: 1.9, color: CT.ink2, whiteSpace: 'pre-wrap',
              }}
              >
                <strong style={{ display: 'block', fontSize: 11, color: CT.mute }}>
                  {t('visit.soap.reviewCurrent')}
                </strong>
                {current[section.id]}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <Button
                size="sm"
                onClick={() => onApply(section.id, 'replace')}
                data-testid={`soap-apply-replace-${section.id}`}
              >
                {t('visit.soap.applyReplace')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onApply(section.id, 'append')}
                data-testid={`soap-apply-append-${section.id}`}
              >
                {t('visit.soap.applyAppend')}
              </Button>
            </div>
          </Card>
        ))}
      </div>
      <AiAnswerNote />
    </BottomSheet>
  );
}

/**
 * «پیشنهاد دستورات» — a checklist. Ticking selects; «افزودن» writes the ticked
 * rows with `origin: ai`, which is a fact about where the row came from and is
 * immutable afterwards (core.md §5.2).
 */
export function OrderSuggestionSheet({ open, onClose, suggestion, onAdd, adding = false }) {
  const { t } = useTranslation();
  const [picked, setPicked] = React.useState({});

  const items = suggestion?.items || [];

  React.useEffect(() => { if (open) setPicked({}); }, [open, suggestion]);

  if (!open) return null;

  const chosen = items.filter((_, i) => picked[i]);

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('visit.orders.suggestTitle')}
      fullHeight
      maxWidth={640}
      closeLabel={t('common.actions.close')}
      data-testid="order-suggest-sheet"
      footer={(
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            onClick={() => onAdd(chosen)}
            disabled={!chosen.length || adding}
            data-testid="order-suggest-add"
          >
            {t('visit.orders.suggestAdd', { count: chosen.length })}
          </Button>
          <Button variant="outline" onClick={onClose}>{t('common.actions.cancel')}</Button>
        </div>
      )}
    >
      <Narrative text={suggestion?.narrative} />
      {!items.length && (
        <p style={{ fontSize: 13, color: CT.mute }}>{t('visit.orders.suggestEmpty')}</p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((item, i) => (
          <Card key={`${item.title}-${i}`} style={{ padding: 10 }}>
            <Checkbox
              checked={!!picked[i]}
              onChange={(v) => setPicked((prev) => ({ ...prev, [i]: !!v }))}
              label={item.title}
              data-testid={`order-suggest-item-${i}`}
            />
            <div style={{ display: 'flex', gap: 6, margin: '6px 0 0 26px', flexWrap: 'wrap' }}>
              <Badge variant="outline">{t(orderTypeKey(item.orderType))}</Badge>
              <Badge variant="outline">{t(urgencyKey(item.urgency))}</Badge>
            </div>
            {item.reason && (
              <p style={{
                margin: '6px 0 0', fontSize: 12, lineHeight: 1.9, color: CT.ink2,
              }}
              >
                {item.reason}
              </p>
            )}
          </Card>
        ))}
      </div>
      <AiAnswerNote />
    </BottomSheet>
  );
}

export default SoapReviewSheet;
